import { useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  buildWhatsappLink,
  publicConfirmLink,
  formatDateES,
} from "@/lib/utils-app";

/**
 * Centralised actions used by the appointment views (Appointments tab,
 * Today dialog, Calendar tab). Each function performs an API call, shows a
 * toast on success/failure, and invokes the `reload` callback so the host
 * component can refresh its data.
 */
export default function useAppointmentActions(reload) {
  const openWhatsapp = useCallback((appt, isReminder) => {
    if (!appt.student?.phone) {
      toast.error("El alumno no tiene teléfono");
      return false;
    }
    const url = buildWhatsappLink({
      phone: appt.student.phone,
      name: appt.student.name,
      date: formatDateES(appt.date),
      time: appt.time,
      endTime: appt.end_time,
      location: appt.pickup_location,
      locationEnd: appt.pickup_location_end,
      confirmLink: publicConfirmLink(appt.id),
      type: isReminder ? "recordatorio" : "cita",
    });
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  }, []);

  const markFlag = useCallback(
    async (appt, field, successMsg) => {
      try {
        await api.patch(`/appointments/${appt.id}`, { [field]: true });
        if (successMsg) toast.success(successMsg);
        await reload?.();
      } catch (err) {
        console.warn(`Could not mark ${field} on appointment`, err);
      }
    },
    [reload]
  );

  const sendWhatsapp = useCallback(
    async (appt) => {
      if (!openWhatsapp(appt, false)) return;
      if (!appt.whatsapp_sent) {
        await markFlag(appt, "whatsapp_sent", "WhatsApp marcado como enviado");
      }
    },
    [openWhatsapp, markFlag]
  );

  const sendReminder = useCallback(
    async (appt) => {
      if (!openWhatsapp(appt, true)) return;
      await markFlag(appt, "reminder_sent", "Recordatorio marcado como enviado");
    },
    [openWhatsapp, markFlag]
  );

  const setStatus = useCallback(
    async (appt, status, extra = {}) => {
      try {
        const payload = { status, ...extra };
        if (status !== "change_requested") {
          payload.requested_date = "";
          payload.requested_time = "";
        }
        await api.patch(`/appointments/${appt.id}`, payload);
        const label =
          status === "confirmed"
            ? "Cita confirmada"
            : status === "cancelled"
            ? (extra.cancel_consumes ? "Cita cancelada · la clase SÍ resta" : "Cita cancelada · la clase NO resta")
            : status === "change_requested"
            ? "Cambio solicitado"
            : "Cita actualizada";
        toast.success(label);
        await reload?.();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    },
    [reload]
  );

  // V1.83 · Cambia a posteriori si una cancelación descuenta la clase.
  const setCancelConsumes = useCallback(
    async (appt, consumes) => {
      try {
        await api.patch(`/appointments/${appt.id}`, { cancel_consumes: consumes });
        toast.success(consumes ? "La clase cancelada SÍ resta del bono" : "La clase cancelada NO resta del bono");
        await reload?.();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    },
    [reload]
  );

  const toggleAttended = useCallback(
    async (appt) => {
      try {
        await api.patch(`/appointments/${appt.id}`, { attended: !appt.attended });
        toast.success(appt.attended ? "Marcada como no asistida" : "Marcada como asistida");
        await reload?.();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    },
    [reload]
  );

  const removeAppt = useCallback(
    async (appt) => {
      if (!window.confirm("¿Eliminar esta cita?")) return;
      try {
        await api.delete(`/appointments/${appt.id}`);
        toast.success("Cita eliminada");
        await reload?.();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    },
    [reload]
  );

  return {
    sendWhatsapp,
    sendReminder,
    setStatus,
    setCancelConsumes,
    toggleAttended,
    removeAppt,
  };
}
